// Shared logic for computing a squad's permanent public URL.
//
// The public URL is generated once at squad creation from attributes that
// never change for that squad's lifetime (birth-year cohort for age-graded
// squads, or the grade name for adult squads) — never from the editable
// age-group dropdown or squad name. See the "Squad Public URL & Permanent
// Identity" plan for the full design.

export const RETROACTIVE_LABEL = '1sts'
export const COLLISION_LABEL_PLACEHOLDERS = ['2nds', 'Division 4', 'The Avengers']

// Grades that look like "U<number>" but don't map to a fixed birth-year
// cohort (treated as adult grades instead).
const AGE_INDEPENDENT_U_GRADES = ['20', '21']

export function slugify(text) {
  return (text || '').trim().toLowerCase()
    .replace(/'/g, '')
    .replace(/[áàâä]/g, 'a').replace(/[éèêë]/g, 'e')
    .replace(/[íìîï]/g, 'i').replace(/[óòôö]/g, 'o').replace(/[úùûü]/g, 'u')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

export function getBirthYearForGrade(gradeText, seasonYear = new Date().getFullYear()) {
  const match = /^U(\d+)$/i.exec((gradeText || '').trim())
  if (!match) return null
  if (AGE_INDEPENDENT_U_GRADES.includes(match[1])) return null
  return seasonYear - Number(match[1])
}

export function getGenderToken(gender) {
  return gender === 'girls' ? 'girls' : 'boys'
}

export function getSportToken(sport) {
  if (sport === 'dual_ladies') return 'dual'
  return sport || 'football'
}

// The part of the slug/identity that's permanent: a birth year for
// age-graded grades, or the slugified grade name for everything else
// (Senior, Intermediate, Junior, Minor, U20, U21, ...).
export function getGradeCore(gradeText, seasonYear = new Date().getFullYear()) {
  const birthYear = getBirthYearForGrade(gradeText, seasonYear)
  if (birthYear != null) return String(birthYear)
  // No grade set at all (age group is optional on some squads) — omit this
  // segment rather than guessing a grade.
  return slugify(gradeText || '')
}

export function buildBaseSquadSlug({ clubSlug, gradeText, gender, sport, seasonYear }) {
  const core = getGradeCore(gradeText, seasonYear)
  return [clubSlug, core, getGenderToken(gender), getSportToken(sport)].filter(Boolean).join('-')
}

// Identity key used to detect collisions — two squads with the same key
// within a club would generate the same base slug.
export function getSquadIdentityKey(squadLike, seasonYear = new Date().getFullYear()) {
  const gradeText = squadLike.current_grade || squadLike.age_group || ''
  return [getGradeCore(gradeText, seasonYear), getGenderToken(squadLike.gender), getSportToken(squadLike.sport)].join('|')
}

// Finds an existing squad (excluding excludeId, e.g. the squad being edited)
// whose identity key matches the candidate's — the "collision" case.
export function findCollidingSquad(existingSquads, candidate, { excludeId, seasonYear } = {}) {
  const candidateKey = getSquadIdentityKey(candidate, seasonYear)
  return existingSquads.find(s => s.id !== excludeId && getSquadIdentityKey(s, seasonYear) === candidateKey) || null
}

// Whether two squad names already read as distinct to a human, e.g.
// "Simonstown U12 Premier" vs "U12 Boys Division 1" — in which case there's
// no need to force an auto-label ("- 1sts") onto either of them just
// because they'd otherwise generate the same base URL. Deliberately strict
// (exact match after normalizing case/whitespace) rather than fuzzy, so it
// only skips the auto-label when the names are genuinely, unambiguously
// already different — not a similarity guess.
export function areNamesIndistinguishable(nameA, nameB) {
  const normalize = s => (s || '').trim().toLowerCase().replace(/\s+/g, ' ')
  return normalize(nameA) === normalize(nameB)
}

export function appendLabelToSlug(baseSlug, label) {
  const labelSlug = slugify(label)
  return labelSlug ? baseSlug + '-' + labelSlug : baseSlug
}

export function appendLabelToName(name, label) {
  return label && label.trim() ? name.trim() + ' - ' + label.trim() : name
}

// Last-resort uniqueness fallback (e.g. two clubs' slugs already collided,
// or a manually-typed override matches an existing squad) — appends -2, -3,
// ... mirroring the existing club-slug uniqueness loop.
export async function findAvailableSlug(supabase, candidateBase) {
  let slug = candidateBase
  let suffix = 2
  while (true) {
    const { data: existing } = await supabase.from('squads').select('id').eq('slug', slug).maybeSingle()
    if (!existing) return slug
    slug = candidateBase + '-' + suffix
    suffix++
  }
}

// Notifies a squad's admin(s) that it was retroactively relabeled because a
// new squad collided with it (e.g. "Senior Boys" -> "Senior Boys - 1sts").
export async function notifySquadRelabeled(supabase, { squad, newName, newSlug, clubName }) {
  const { data: clubAdmins } = await supabase
    .from('profiles').select('email').eq('club_id', squad.club_id).eq('role', 'club_admin')
  const { data: squadAdminMembers } = await supabase
    .from('squad_members').select('profile_id').eq('squad_id', squad.id).eq('role', 'admin')

  let squadAdminEmails = []
  if (squadAdminMembers && squadAdminMembers.length > 0) {
    const ids = squadAdminMembers.map(m => m.profile_id)
    const { data: squadAdminProfiles } = await supabase.from('profiles').select('email').in('id', ids)
    squadAdminEmails = (squadAdminProfiles || []).map(p => p.email).filter(Boolean)
  }

  const clubAdminEmails = (clubAdmins || []).map(p => p.email).filter(Boolean)
  const recipients = Array.from(new Set([...clubAdminEmails, ...squadAdminEmails]))
  if (recipients.length === 0) return

  await supabase.functions.invoke('send-invite-email', {
    body: {
      type: 'squad_relabeled',
      toEmails: recipients,
      clubName,
      oldName: squad.name,
      newName,
      oldUrl: squad.slug ? 'https://gaamatchtracker.ie/public/' + squad.slug : null,
      newUrl: 'https://gaamatchtracker.ie/public/' + newSlug
    }
  })
}
